import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, ShoppingBag, CreditCard, Package, Globe, Check, Crosshair, Eye, Shield, Zap, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { cartStorage } from '../utils/cart';
import { get_payment_link } from '../utils/pixCodes';
import { useGeo } from '../contexts/GeoContext';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from '../utils/translations';

const Cart = () => {
  const navigate = useNavigate();
  const { isBrazil, currency, getIntlLink } = useGeo();
  const tr = useTranslation(isBrazil);
  const [cart, setCart] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    setCart(cartStorage.getCart());
  }, []);

  const removeItem = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    cartStorage.saveCart(newCart);
    toast({ title: tr('itemRemoved'), description: tr('itemRemovedDesc') });
  };

  const getTotal = () => cart.reduce((total, item) => total + item.price, 0);
  const getTotalDiscount = () => cart.reduce((total, item) => total + ((item.originalPrice || item.price) - item.price), 0);
  const getOriginalTotal = () => cart.reduce((total, item) => total + (item.originalPrice || item.price), 0);

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-black py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gray-900 rounded-2xl p-16 border-2 border-red-900/30">
            <ShoppingBag className="w-32 h-32 text-red-600/30 mx-auto mb-8" />
            <h1 className="text-4xl font-bold text-white mb-4 gaming-title" data-testid="empty-cart-title">{tr('emptyCart')}</h1>
            <p className="text-gray-400 text-lg mb-8">{tr('emptyCartDesc')}</p>
            <Link to="/produtos">
              <Button className="bg-red-600 hover:bg-red-700 text-white font-bold px-10 py-6 text-lg border-2 border-red-500 shadow-lg gaming-title" data-testid="view-products-btn">
                <Package className="w-5 h-5 mr-2" />
                {tr('viewProducts')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-16 px-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-12 gaming-title glow-red text-center md:text-left" data-testid="cart-title">
          {tr('shoppingCart')}
        </h1>
        
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item, index) => (
              <Card key={`${item.planId}-${item.timestamp}`} className="bg-gray-900 border-2 border-red-900/30 hover:border-red-600/50 transition-all card-premium">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-bold text-xl text-white gaming-title mb-2">{item.productName}</h3>
                      <div className="space-y-1">
                        <p className="text-gray-400">
                          <span className="text-red-600 font-semibold">{tr('plan')}:</span> {item.planType}
                        </p>
                        <p className="text-gray-400">
                          <span className="text-red-600 font-semibold">{tr('validity')}:</span> {item.validity}
                        </p>
                      </div>
                    </div>
                    <div className="text-right ml-6">
                      {item.originalPrice && item.originalPrice > item.price && (
                        <p className="text-gray-500 text-sm line-through">{item.currency || currency} {item.originalPrice.toFixed(2)}</p>
                      )}
                      <p className="text-3xl font-bold text-red-600 gaming-title mb-1">{item.currency || currency} {item.price.toFixed(2)}</p>
                      {item.originalPrice && item.originalPrice > item.price && (
                        <p className="text-green-400 text-xs font-semibold">{tr('save')} {item.currency || currency} {(item.originalPrice - item.price).toFixed(2)}</p>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="text-gray-400 hover:text-red-600 hover:bg-red-600/10 transition-all mt-2"
                        data-testid={`remove-item-${index}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* What's Included */}
            <Card className="bg-gray-900 border-2 border-red-900/30">
              <CardContent className="p-6 space-y-5">
                {/* Garantias */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-green-400" />
                    <h3 className="text-green-400 font-bold text-sm uppercase tracking-wider">Garantias</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-200 text-sm">Atualizações garantidas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-200 text-sm">101% Safe - Sem risco de perder sua conta</span>
                    </div>
                  </div>
                </div>

                {/* Funções */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Crosshair className="w-5 h-5 text-red-400" />
                    <h3 className="text-red-400 font-bold text-sm uppercase tracking-wider">Funções Inclusas</h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {['Precision', 'Aimbot Legit', 'Aimbot Rage', 'Aimbot Full Rage', 'No Recoil', 'Aimlock', 'Magnet', 'Ghost'].map((fn) => (
                      <div key={fn} className="flex items-center gap-2">
                        <Zap className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                        <span className="text-gray-200 text-xs sm:text-sm">{fn}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-500 text-xs mt-2 italic">Aimbot Legit: você escolhe quantos peito dar antes de dar capa</p>
                  <p className="text-gray-500 text-xs italic">No Recoil: as balas vão reto sem espalhar</p>
                </div>

                {/* ESP */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-5 h-5 text-blue-400" />
                    <h3 className="text-blue-400 font-bold text-sm uppercase tracking-wider">Todas as ESP Inclusas</h3>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {['Lines', 'Nome', 'Vida', 'Box', 'Fill Box', 'Box Canto', 'Distância', 'Arma Usada', 'FOV Regulável'].map((esp) => (
                      <div key={esp} className="flex items-center gap-1.5">
                        <Eye className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        <span className="text-gray-300 text-xs">{esp}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Platform specific */}
                {cart.some(item => item.productName?.includes('ANDROID')) && (
                  <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 font-bold text-xs uppercase">Exclusivo Android</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      <span className="text-gray-200 text-xs flex items-center gap-1"><Check className="w-3 h-3 text-green-400" />Não precisa de ROOT</span>
                      <span className="text-gray-200 text-xs flex items-center gap-1"><Check className="w-3 h-3 text-green-400" />Não precisa de Virtual Machine</span>
                      <span className="text-gray-200 text-xs flex items-center gap-1"><Check className="w-3 h-3 text-green-400" />Android 9, 10, 11, 12, 13, 14, 15, 16</span>
                      <span className="text-gray-200 text-xs flex items-center gap-1 font-bold"><Zap className="w-3 h-3 text-green-400" />Baixou, instalou, jogou!</span>
                    </div>
                  </div>
                )}

                {cart.some(item => item.productName?.includes('IOS')) && (
                  <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-blue-400" />
                      <span className="text-blue-400 font-bold text-xs uppercase">Exclusivo iOS</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      <span className="text-gray-200 text-xs flex items-center gap-1"><Check className="w-3 h-3 text-blue-400" />Fácil Instalação</span>
                      <span className="text-gray-200 text-xs flex items-center gap-1"><Check className="w-3 h-3 text-blue-400" />Não precisa de GBox</span>
                      <span className="text-gray-200 text-xs flex items-center gap-1"><Zap className="w-3 h-3 text-blue-400" />Só baixar, instalar e jogar</span>
                      <span className="text-gray-200 text-xs flex items-center gap-1"><Shield className="w-3 h-3 text-blue-400" />Safe no Brasil e na Índia</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="bg-gray-900 border-2 border-red-900/30 sticky top-6">
              <CardHeader className="border-b border-red-900/30">
                <CardTitle className="text-white gaming-title text-xl">{tr('orderSummary')}</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4">
                  <div className="flex justify-between text-gray-400">
                    <span>{tr('subtotal')}</span>
                    <span className="font-semibold">{currency} {getOriginalTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>{tr('discount')}</span>
                    <span className="font-semibold text-green-500">- {currency} {getTotalDiscount().toFixed(2)}</span>
                  </div>
                  <div className="border-t border-red-900/30 pt-4">
                    <div className="flex justify-between">
                      <span className="font-bold text-lg text-white gaming-title">TOTAL</span>
                      <span className="text-3xl font-bold text-red-600 gaming-title">{currency} {getTotal().toFixed(2)}</span>
                    </div>
                  </div>
                  {getTotalDiscount() > 0 && (
                    <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-3 text-center">
                      <p className="text-green-400 text-sm font-bold">{tr('youreSaving')} {currency} {getTotalDiscount().toFixed(2)}!</p>
                    </div>
                  )}
                </div>
                
                {/* Checkout Button */}
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-6 text-lg border-2 border-green-500 shadow-lg hover:shadow-green-600/50 transition-all gaming-title"
                  onClick={handleCheckout}
                  data-testid="checkout-btn"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  {tr('checkoutBrazil')}
                </Button>
                <p className="text-gray-500 text-xs text-center">{isBrazil ? tr('pixBoleto') : tr('creditPaypal')}</p>
                
                <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20">
                  <p className="text-xs text-gray-400 text-center leading-relaxed">
                    {tr('securePayment')}<br />
                    {tr('instantDeliveryAfter')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
